// Utils
import { FlowEventHandlers } from '../../../../flow-event';
import { TrackingOperation } from '@shopee-user/tracking';
import { getLoginPageTypeAndSchema } from '@shopee-user/business-customization-logic';
import { monitor } from '../../../../../platform/monitoring';
import {
  convertChannelToTrackingInfo,
  toMonitoringLoginMethod,
  toMonitoringBusinessPage,
} from './utils';

// Constants & Enums
import { LoginTrackingPoint } from '@shopee-user/tracking-constants';
import { BusinessPerfMetric } from '../../../../../platform/monitoring/performance/enums';
import { ThirdpartyAuthOperation } from '@tob/shared/thirdparty-auth/service/constants';

// Monitoring
import { sendLoginResult } from '@tob/shared/monitoring/business-metric';
import { Status, AccType } from '@tob/shared/monitoring/business-metric/enums';

// Types
import type {
  ThirdPartyLoginFlowEventDefinition,
  ThirdPartyLoginFlowEventState,
} from './types';
import type { AuthenticateChannel } from '@tob/shared/thirdparty-auth/core/types';

export const TRACKER_NAME = 'third-party-login';

export const createInitialState = (): ThirdPartyLoginFlowEventState => {
  return {
    loginMethod: '',
  };
};

export function createTracker() {
  // this is sent when user clicks on the login with xxx button
  const trackAccountAttemptLogin = () => {
    monitor.sendAccumulate({
      name: BusinessPerfMetric.ThirdPartyAccountAttemptLogin,
    });
  };

  // this is sent when login is successful / failed
  const trackAccountLogin = ({ errorCode }: { errorCode?: number }) => {
    monitor.sendAccumulate({
      name: BusinessPerfMetric.ThirdPartyAccountLogin,
      data: {
        errorCode,
      },
    });
  };

  const handlers: FlowEventHandlers<
    ThirdPartyLoginFlowEventDefinition,
    ThirdPartyLoginFlowEventState
  > = {
    onThirdPartyLoginComponentView: ({ channel }) => {
      const { loginMethod } = convertChannelToTrackingInfo(channel);
      // update state
      return {
        loginMethod,
      };
    },
    onThirdPartyLoginClick: ({ channel }) => {
      return ({ tools }) => {
        const { targetType, trackingPoint } = convertChannelToTrackingInfo(
          channel
        );
        tools.sendBITrackingEvent({
          operation: TrackingOperation.Click,
          target_type: targetType,
          ...getLoginPageTypeAndSchema(trackingPoint as LoginTrackingPoint), // spx_login, new schema
        });
        trackAccountAttemptLogin();
      };
    },
    onThirdPartyLoginActionSuccess: ({
      channel,
      accountType,
      entryPoint,
      operation,
    }) => {
      return ({ tools }) => {
        const { loginMethod } = convertChannelToTrackingInfo(
          channel as AuthenticateChannel
        );
        tools.sendBITrackingEvent({
          operation: TrackingOperation.LoginSuccessAction,
          data: { login_method: loginMethod, account_type: accountType },
          ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginSuccessAction), // spx_login, existing schema
        });
        trackAccountLogin({});

        // Monitoring: only a real login counts, never a Bind/Unbind operation.
        if (operation === ThirdpartyAuthOperation.Login) {
          const monitoringLoginMethod = toMonitoringLoginMethod(channel);
          const businessPage = toMonitoringBusinessPage(entryPoint);
          if (!monitoringLoginMethod || !businessPage) return;

          sendLoginResult({
            status: Status.Success,
            login_method: monitoringLoginMethod,
            business_page: businessPage,
            acc_type: AccType.Tob,
          });
        }
      };
    },
    onThirdPartyLoginActionFail: ({
      channel,
      errorCode,
      entryPoint,
      apiSource,
      operation: thirdPartyAuthOperation,
    }) => {
      return ({ tools }) => {
        const { loginMethod } = convertChannelToTrackingInfo(
          channel as AuthenticateChannel
        );
        tools.sendBITrackingEvent({
          operation: TrackingOperation.LoginFailAction,
          data: {
            login_method: loginMethod,
            error_code: errorCode,
          },
          ...getLoginPageTypeAndSchema(LoginTrackingPoint.LoginFailAction), // spx_login, new schema
        });
        trackAccountLogin({ errorCode });

        // Monitoring: only a real login counts, never a Bind/Unbind operation.
        if (thirdPartyAuthOperation === ThirdpartyAuthOperation.Login) {
          const monitoringLoginMethod = toMonitoringLoginMethod(channel);
          const businessPage = toMonitoringBusinessPage(entryPoint);
          if (!monitoringLoginMethod || !businessPage) return;

          sendLoginResult({
            status: Status.Failed,
            login_method: monitoringLoginMethod,
            business_page: businessPage,
            acc_type: AccType.Tob,
            error: errorCode,
            api_source: apiSource,
          });
        }
      };
    },
  };

  return {
    name: TRACKER_NAME,
    handlers,
  };
}
